import React, { useState } from 'react';
import { 
  Trophy, Medal, XCircle, ChevronDown, ChevronUp, 
  CheckCircle, AlertTriangle, ShieldCheck, Globe2,
  TrendingUp, Users, Scale, Sparkles, FileText,
  ThumbsUp, ThumbsDown, Lightbulb, Globe, AtSign,
  BarChart3, Zap, Target, Star, Award, Ban,
  ExternalLink, Copy, Info, Languages, BookOpen,
  Bookmark, Hash, MapPin, Calendar, Briefcase, Rocket,
  Building2, DollarSign, Clock, Map
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 📊 COMPARISON DASHBOARD v2.0 - Enhanced Multi-Brand Comparison View
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * UPGRADE: Now includes per-brand mini-reports with:
 * - Strengths & Risks (Pros/Cons)
 * - Alternative Names for rejected brands
 * - Digital Presence (Domain + Social)
 * - Dimension breakdown
 * - Rich comparison verdict with decision matrix
 * - Universal Linguistic Analysis (NEW)
 */

// ═══════════════════════════════════════════════════════════════════════════════
// 🌍 UNIVERSAL LINGUISTIC ANALYSIS SECTION (PAGE 3)
// ═══════════════════════════════════════════════════════════════════════════════

const LinguisticComparisonSection = ({ brands }) => {
  const brandsWithLinguistic = brands.filter(b => 
    b.universal_linguistic_analysis && 
    b.universal_linguistic_analysis._analyzed_by !== 'fallback'
  );

  if (brandsWithLinguistic.length === 0) return null;

  const getClassificationColor = (category) => {
    switch(category?.toUpperCase()) {
      case 'FANCIFUL': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'ARBITRARY': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'SUGGESTIVE': return 'bg-violet-100 text-violet-700 border-violet-300';
      case 'DESCRIPTIVE': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'GENERIC': return 'bg-red-100 text-red-700 border-red-300';
      case 'TRUE-COINED': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      default: return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  const getAlignmentColor = (score) => {
    if (score >= 8) return { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500' };
    if (score >= 5) return { bg: 'bg-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' };
    return { bg: 'bg-red-100', text: 'text-red-700', bar: 'bg-red-500' };
  };

  return (
    <Card className="overflow-hidden shadow-xl">
      <CardHeader className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 text-white">
        <CardTitle className="flex items-center gap-3 text-xl">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <Languages className="w-6 h-6" />
          </div>
          <div>
            <span>Universal Linguistic Analysis</span>
            <p className="text-sm font-normal text-white/80 mt-0.5">
              Deep meaning detection across all world languages
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Quick Comparison Table */}
        <div className="overflow-x-auto mb-8">
          <table className="w-full">
            <thead className="bg-violet-50 border-b-2 border-violet-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-bold text-violet-800 uppercase">Brand Name</th>
                <th className="text-center py-3 px-4 text-sm font-bold text-violet-800 uppercase">Has Meaning?</th>
                <th className="text-center py-3 px-4 text-sm font-bold text-violet-800 uppercase">Origin Languages</th>
                <th className="text-center py-3 px-4 text-sm font-bold text-violet-800 uppercase">Name Type</th>
                <th className="text-center py-3 px-4 text-sm font-bold text-violet-800 uppercase">Classification</th>
                <th className="text-center py-3 px-4 text-sm font-bold text-violet-800 uppercase">Business Alignment</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((brand, idx) => {
                const ling = brand.universal_linguistic_analysis;
                const hasMeaning = ling?.has_linguistic_meaning;
                const languages = ling?.linguistic_analysis?.languages_detected || [];
                const nameType = ling?.classification?.name_type || 'Unknown';
                const alignment = ling?.business_alignment?.alignment_score || 0;
                const alignColors = getAlignmentColor(alignment);
                
                // Determine display category
                let displayCategory = ling?.classification?.trademark_category || 
                                     brand.brand_classification?.category || 
                                     (hasMeaning ? 'SUGGESTIVE' : 'FANCIFUL');
                if (hasMeaning && displayCategory === 'FANCIFUL') {
                  displayCategory = alignment >= 6 ? 'SUGGESTIVE' : 'ARBITRARY';
                }

                return (
                  <tr key={brand.brand_name} className={`border-b border-violet-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-violet-50/30'}`}>
                    <td className="py-4 px-4">
                      <span className="font-bold text-slate-800">{brand.brand_name}</span>
                    </td>
                    <td className="text-center py-4 px-4">
                      {ling ? (
                        <Badge className={hasMeaning ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}>
                          {hasMeaning ? '🌍 Yes' : '✨ No (Coined)'}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4">
                      {languages.length > 0 ? (
                        <span className="text-sm text-violet-700 font-medium">{languages.join(', ')}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4">
                      <Badge variant="outline" className="font-medium">{nameType}</Badge>
                    </td>
                    <td className="text-center py-4 px-4">
                      <Badge className={`${getClassificationColor(displayCategory)} font-bold border`}>
                        {displayCategory}
                      </Badge>
                    </td>
                    <td className="text-center py-4 px-4">
                      {ling ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${alignColors.bar} transition-all`}
                              style={{ width: `${alignment * 10}%` }}
                            />
                          </div>
                          <span className={`font-bold ${alignColors.text}`}>{alignment}/10</span>
                        </div>
                      ) : (
                        <span className="text-slate-400">N/A</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Per-Brand Deep Linguistic Analysis */}
        <div className="space-y-6">
          {brands.map((brand) => {
            const ling = brand.universal_linguistic_analysis;
            if (!ling || ling._analyzed_by === 'fallback') return null;

            const hasMeaning = ling.has_linguistic_meaning;
            const lingData = ling.linguistic_analysis || {};
            const cultural = ling.cultural_significance || {};
            const alignment = ling.business_alignment || {};
            const concerns = ling.potential_concerns || [];
            const similarBrands = ling.similar_successful_brands || [];
            const decomposition = lingData.decomposition || {};

            return (
              <div 
                key={brand.brand_name}
                className={`rounded-2xl border-2 p-5 ${
                  hasMeaning 
                    ? 'border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50' 
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl ${hasMeaning ? 'bg-violet-100' : 'bg-slate-100'} flex items-center justify-center`}>
                      <span className="text-2xl">{hasMeaning ? '🌍' : '✨'}</span>
                    </div>
                    <div>
                      <h4 className="font-black text-lg text-slate-900">{brand.brand_name}</h4>
                      <p className="text-sm text-slate-600">
                        {hasMeaning 
                          ? `Origin: ${(lingData.languages_detected || []).join(', ') || 'Multiple languages'}`
                          : 'Truly coined name - no existing meaning'}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${hasMeaning ? 'bg-violet-100 text-violet-700' : 'bg-emerald-100 text-emerald-700'} font-bold`}>
                    {ling.classification?.name_type || 'Unknown'}
                  </Badge>
                </div>

                {/* Morphological Breakdown */}
                {hasMeaning && decomposition.can_be_decomposed && (
                  <div className="bg-white rounded-xl p-4 mb-4 border border-violet-100">
                    <div className="flex items-center gap-2 mb-3">
                      <Hash className="w-4 h-4 text-violet-600" />
                      <h5 className="font-bold text-slate-700 text-sm uppercase">Morphological Breakdown</h5>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {(decomposition.parts || []).map((part, idx) => {
                        const partInfo = decomposition.part_meanings?.[part] || {};
                        return (
                          <div key={idx} className="flex items-center">
                            <div className="bg-violet-100 text-violet-800 font-bold px-3 py-1.5 rounded-lg text-sm">
                              {part}
                              <span className="text-violet-600 font-normal ml-1">
                                = {typeof partInfo === 'object' ? partInfo.meaning : partInfo}
                              </span>
                            </div>
                            {idx < (decomposition.parts || []).length - 1 && (
                              <span className="mx-2 text-slate-400">+</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {decomposition.combined_meaning && (
                      <p className="text-sm">
                        <span className="font-bold text-slate-700">Combined Meaning:</span>{' '}
                        <span className="text-slate-600 italic">&ldquo;{decomposition.combined_meaning}&rdquo;</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Cultural Significance */}
                {hasMeaning && cultural.has_cultural_reference && (
                  <div className="bg-white rounded-xl p-4 mb-4 border border-amber-100">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-amber-600" />
                      <h5 className="font-bold text-slate-700 text-sm uppercase">Cultural & Historical Significance</h5>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div>
                        <p className="text-xs text-slate-500 font-medium mb-1">Reference Type</p>
                        <p className="font-semibold text-slate-800 flex items-center gap-1">
                          <span>
                            {cultural.reference_type === 'Mythological' ? '🏛️' : 
                             cultural.reference_type === 'Religious' ? '🙏' :
                             cultural.reference_type === 'Historical' ? '📜' : '📚'}
                          </span>
                          {cultural.reference_type}
                        </p>
                      </div>
                      {cultural.source_text_or_origin && (
                        <div>
                          <p className="text-xs text-slate-500 font-medium mb-1">Source</p>
                          <p className="font-semibold text-slate-800">{cultural.source_text_or_origin}</p>
                        </div>
                      )}
                      {cultural.sentiment && (
                        <div>
                          <p className="text-xs text-slate-500 font-medium mb-1">Sentiment</p>
                          <Badge className={`${
                            cultural.sentiment === 'Sacred' || cultural.sentiment === 'Heroic' ? 'bg-amber-100 text-amber-700' :
                            cultural.sentiment === 'Auspicious' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {cultural.sentiment}
                          </Badge>
                        </div>
                      )}
                      {cultural.regions_of_recognition?.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 font-medium mb-1">Recognized In</p>
                          <p className="text-sm text-slate-700">{cultural.regions_of_recognition.join(', ')}</p>
                        </div>
                      )}
                    </div>
                    {cultural.details && (
                      <p className="mt-3 text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
                        {cultural.details}
                      </p>
                    )}
                  </div>
                )}

                {/* Business Alignment */}
                <div className={`bg-white rounded-xl p-4 border ${
                  (alignment.alignment_score || 0) >= 8 ? 'border-emerald-200' :
                  (alignment.alignment_score || 0) >= 5 ? 'border-amber-200' : 'border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-4 h-4 text-blue-600" />
                    <h5 className="font-bold text-slate-700 text-sm uppercase">Business Alignment Analysis</h5>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-black text-slate-900">
                        {alignment.alignment_score || 0}
                        <span className="text-lg text-slate-400">/10</span>
                      </p>
                      <p className={`text-sm font-semibold ${
                        (alignment.alignment_score || 0) >= 8 ? 'text-emerald-600' :
                        (alignment.alignment_score || 0) >= 5 ? 'text-amber-600' : 'text-red-600'
                      }`}>
                        {alignment.alignment_level || 'Unknown'}
                      </p>
                    </div>
                    <div className="w-20 h-20">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="40" cy="40" r="32" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                        <circle 
                          cx="40" cy="40" r="32" 
                          stroke={(alignment.alignment_score || 0) >= 8 ? '#10b981' : (alignment.alignment_score || 0) >= 5 ? '#f59e0b' : '#ef4444'}
                          strokeWidth="6" 
                          fill="none"
                          strokeDasharray={`${(alignment.alignment_score || 0) * 20.1} 201`}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                  {alignment.thematic_connection && (
                    <p className="text-sm text-slate-600 mt-3">
                      <span className="font-bold">Thematic Connection:</span> {alignment.thematic_connection}
                    </p>
                  )}
                  
                  {/* Regional Understanding */}
                  {alignment.customer_understanding && (
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      {alignment.customer_understanding.instant_recognition_regions?.length > 0 && (
                        <div className="bg-emerald-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-emerald-700 mb-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Instant Recognition
                          </p>
                          <p className="text-xs text-emerald-600">
                            {alignment.customer_understanding.instant_recognition_regions.join(', ')}
                          </p>
                        </div>
                      )}
                      {alignment.customer_understanding.needs_explanation_regions?.length > 0 && (
                        <div className="bg-amber-50 rounded-lg p-3">
                          <p className="text-xs font-bold text-amber-700 mb-1 flex items-center gap-1">
                            <Info className="w-3 h-3" /> Needs Explanation
                          </p>
                          <p className="text-xs text-amber-600">
                            {alignment.customer_understanding.needs_explanation_regions.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Potential Concerns */}
                {concerns.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <h5 className="font-bold text-red-700 text-sm uppercase">Potential Concerns</h5>
                    </div>
                    <div className="space-y-2">
                      {concerns.slice(0, 3).map((concern, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-sm">
                          <Badge className={`shrink-0 ${
                            concern.severity === 'High' ? 'bg-red-100 text-red-700' :
                            concern.severity === 'Medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>{concern.severity}</Badge>
                          <span className="text-slate-700">{concern.details}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Similar Successful Brands */}
                {similarBrands.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-blue-600" />
                      <h5 className="font-bold text-blue-700 text-sm uppercase">Similar Successful Brand Patterns</h5>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {similarBrands.slice(0, 3).map((sb, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-2 border border-blue-100">
                          <p className="font-bold text-slate-800 text-sm">{sb.brand}</p>
                          <p className="text-xs text-blue-600">{sb.meaning_origin}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Executive Summary */}
                {ling.executive_summary && (
                  <div className="bg-gradient-to-r from-violet-100 to-fuchsia-100 rounded-xl p-4 mt-4 border border-violet-200">
                    <p className="text-sm text-slate-700">
                      <span className="font-bold">💡 Summary:</span> {ling.executive_summary}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎯 UNIFIED COMPETITIVE LANDSCAPE SECTION
// ═══════════════════════════════════════════════════════════════════════════════

const CompetitiveLandscapeSection = ({ brands, category }) => {
  // Get first brand with competitive data
  const brandWithData = brands.find(b => 
    b.country_competitor_analysis?.length > 0 && 
    b.country_competitor_analysis[0]?.competitors?.length > 0
  ) || brands.find(b => 
    b.competitor_analysis?.competitors?.length > 0
  );

  // Early return if no competitive data at all
  if (!brandWithData) {
    console.log('CompetitiveLandscapeSection: No brand with competitor data found');
    return null;
  }

  const countryCompetitorAnalysis = brandWithData.country_competitor_analysis || [];
  const globalCompetitors = brandWithData.competitor_analysis?.competitors || [];
  
  // Check if we have any actual data to display
  const hasData = countryCompetitorAnalysis.some(cd => cd?.competitors?.length > 0) || globalCompetitors.length > 0;
  
  if (!hasData) {
    console.log('CompetitiveLandscapeSection: No competitors data found');
    return null;
  }

  // Get display category
  const displayCategory = category || brandWithData.strategic_classification?.split(' ')[0] || 'your category';

  // Parse axis labels
  const parseAxisLabels = (axisLabel) => {
    if (!axisLabel) return { low: 'Low', high: 'High' };
    const parts = axisLabel.split(':');
    if (parts.length < 2) return { low: 'Low', high: 'High' };
    const rangePart = parts[1].trim();
    const rangeMatch = rangePart.match(/(.+?)\s*[→→-]\s*(.+)/);
    if (rangeMatch) {
      return { low: rangeMatch[1].trim(), high: rangeMatch[2].trim() };
    }
    return { low: 'Budget', high: 'Premium' };
  };

  // Render a single positioning matrix - EXACT copy from Dashboard.js
  const renderMatrix = (analysis, title, showFlag = false) => {
    const comps = analysis.competitors || [];
    const xAxis = parseAxisLabels(analysis.x_axis_label);
    const yAxis = parseAxisLabels(analysis.y_axis_label);
    
    // Generate quadrant labels
    const quadrants = {
      topLeft: `${xAxis.low} + ${yAxis.high}`,
      topRight: `${xAxis.high} + ${yAxis.high}`,
      bottomLeft: `${xAxis.low} + ${yAxis.low}`,
      bottomRight: `${xAxis.high} + ${yAxis.low}`
    };
    
    return (
      <div key={title} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            {title}
          </h4>
          {showFlag && analysis.country_flag && (
            <span className="text-3xl">{analysis.country_flag}</span>
          )}
        </div>
        
        {/* Axis Labels Header */}
        <div className="text-center mb-4 p-2 bg-slate-50 rounded-lg">
          <p className="text-xs text-slate-500">
            <span className="font-semibold">X-Axis:</span> {analysis.x_axis_label || 'Price: Budget → Luxury'} | 
            <span className="font-semibold ml-2">Y-Axis:</span> {analysis.y_axis_label || 'Style: Classic → Modern'}
          </p>
        </div>
        
        {/* Visual Matrix - Strategic Positioning Map */}
        <div className="relative bg-gradient-to-br from-slate-50 to-white border-2 border-slate-200 rounded-xl p-4" style={{ height: '300px' }}>
          {/* Grid lines - Axes */}
          <div className="absolute inset-4 border-l-2 border-b-2 border-slate-300"></div>
          {/* Center cross lines */}
          <div className="absolute left-1/2 top-4 bottom-4 border-l border-dashed border-slate-300"></div>
          <div className="absolute left-4 right-4 top-1/2 border-t border-dashed border-slate-300"></div>
          
          {/* Quadrant Labels */}
          <div className="absolute top-5 left-5 text-[10px] text-slate-400 max-w-[80px] leading-tight">{quadrants.topLeft}</div>
          <div className="absolute top-5 right-5 text-[10px] text-emerald-600 text-right max-w-[80px] leading-tight font-semibold">{quadrants.topRight} ✓</div>
          <div className="absolute bottom-5 left-5 text-[10px] text-slate-400 max-w-[80px] leading-tight">{quadrants.bottomLeft}</div>
          <div className="absolute bottom-5 right-5 text-[10px] text-slate-400 text-right max-w-[80px] leading-tight">{quadrants.bottomRight}</div>
          
          {/* Axis End Labels */}
          <div className="absolute bottom-0 left-4 text-[9px] text-slate-500">{xAxis.low}</div>
          <div className="absolute bottom-0 right-4 text-[9px] text-slate-500">{xAxis.high}</div>
          <div className="absolute top-4 left-0 text-[9px] text-slate-500 -rotate-90 origin-left translate-y-4">{yAxis.high}</div>
          <div className="absolute bottom-4 left-0 text-[9px] text-slate-500 -rotate-90 origin-left translate-y-4">{yAxis.low}</div>
          
          {/* Empty state message */}
          {comps.length === 0 && !analysis.user_brand_position && (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="text-slate-400 text-sm">Competitor data not available</p>
            </div>
          )}
          
          {/* Plot competitors as numbered dots - GREY CIRCLES */}
          {comps.slice(0, 6).map((comp, i) => {
            const x = (comp.x_coordinate || 50) / 100 * 80 + 10;
            const y = 100 - ((comp.y_coordinate || 50) / 100 * 80 + 10);
            return (
              <div
                key={i}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 group z-10"
                style={{ left: `${x}%`, top: `${y}%` }}
              >
                <div className="w-8 h-8 bg-slate-500 hover:bg-slate-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md transition-all cursor-pointer">
                  {i + 1}
                </div>
                <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap z-20 shadow-lg">
                  <span className="font-semibold">{comp.name}</span>
                  {comp.quadrant && <span className="text-slate-300 ml-1">• {comp.quadrant?.replace(/_/g, ' ')}</span>}
                </div>
              </div>
            );
          })}
          
          {/* User brand position - GOLD/HIGHLIGHTED */}
          {analysis.user_brand_position && (
            <div
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20 group"
              style={{
                left: `${(analysis.user_brand_position.x_coordinate || analysis.user_brand_position.x || 70) / 100 * 80 + 10}%`,
                top: `${100 - ((analysis.user_brand_position.y_coordinate || analysis.user_brand_position.y || 70) / 100 * 80 + 10)}%`
              }}
            >
              <div className="w-10 h-10 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center text-amber-900 text-[10px] font-bold shadow-lg border-2 border-white animate-pulse">
                YOU
              </div>
              {analysis.user_brand_position.quadrant && (
                <div className="hidden group-hover:block absolute top-full left-1/2 transform -translate-x-1/2 mt-1 px-2 py-1 bg-amber-600 text-white text-xs rounded whitespace-nowrap z-20 shadow-lg">
                  <span className="font-semibold">{analysis.user_brand_position.quadrant}</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Legend */}
        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
          <div className="flex flex-wrap items-center gap-3">
            {/* User Brand Legend */}
            <div className="flex items-center gap-2 text-xs border-r border-slate-300 pr-3">
              <div className="w-5 h-5 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full flex items-center justify-center text-amber-900 text-[8px] font-bold">Y</div>
              <span className="text-slate-700 font-semibold">Your Brand</span>
            </div>
            {/* Competitors Legend */}
            {comps.slice(0, 6).map((comp, i) => (
              <div key={i} className="flex items-center gap-1 text-xs">
                <div className="w-5 h-5 bg-slate-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold">{i + 1}</div>
                <span className="text-slate-600">{comp.name}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Market Insights - White Space & Strategic */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
          {analysis.white_space_analysis && (
            <div className="p-3 rounded-lg border bg-cyan-50 border-cyan-200">
              <p className="text-xs font-bold text-cyan-700 mb-1">White Space Opportunity</p>
              <p className="text-xs text-slate-600">{analysis.white_space_analysis.replace(/\*\*/g, '').replace(/🟡|🟢|🔴/g, '')}</p>
            </div>
          )}
          {analysis.strategic_advantage && (
            <div className="p-3 rounded-lg border bg-violet-50 border-violet-200">
              <p className="text-xs font-bold text-violet-700 mb-1">Strategic Advantage</p>
              <p className="text-xs text-slate-600">{analysis.strategic_advantage.replace(/\*\*/g, '')}</p>
            </div>
          )}
        </div>
        
        {/* Market Entry */}
        {analysis.market_entry_recommendation && (
          <div className="mt-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
            <p className="text-xs font-bold text-emerald-700 mb-1">Market Entry Recommendation</p>
            <p className="text-xs text-slate-600">{analysis.market_entry_recommendation.replace(/\*\*/g, '')}</p>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="overflow-hidden shadow-xl">
      <CardHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 text-white">
        <CardTitle className="flex items-center gap-3 text-xl">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <TrendingUp className="w-6 h-6" />
          </div>
          <div>
            <span>Competitive Landscape</span>
            <p className="text-sm font-normal text-white/80 mt-0.5">
              Market context for {displayCategory} • Same for all brand names
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        
        {/* Explanation Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Why is this the same for all brands?</strong> All your brand names compete in the same market 
            ({displayCategory}) against the same competitors. The landscape shows your market context.
          </div>
        </div>

        {/* Render matrices for each country */}
        {countryCompetitorAnalysis.filter(ca => ca?.competitors?.length > 0).map((analysis, idx) => (
          <div key={idx}>
            {renderMatrix(analysis, `${analysis.country || 'Market'} Strategic Positioning Matrix`, true)}
          </div>
        ))}

        {/* Strategic Insight */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200">
          <h5 className="font-bold text-indigo-800 mb-2 flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Strategic Insight
          </h5>
          <p className="text-sm text-indigo-700">
            Choose a brand name that is <strong>phonetically distinct</strong> from existing competitors 
            to avoid consumer confusion and trademark conflicts. The numbered dots show where competitors 
            are positioned - aim for the white space areas.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🏆 ENHANCED WINNER CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const WinnerCard = ({ winner, rankings, backup }) => {
  if (!winner) return null;

  return (
    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200 shadow-xl overflow-hidden">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Trophy Icon */}
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-lg flex-shrink-0">
            <Trophy className="w-10 h-10 text-white" />
          </div>

          {/* Winner Info */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-bold text-emerald-600 uppercase tracking-wide">🏆 Recommended Winner</span>
            </div>
            <h2 className="text-3xl font-black text-slate-800 mb-2">
              &ldquo;{winner.brand_name}&rdquo;
            </h2>
            <p className="text-slate-600 mb-4">
              {winner.summary || `Best overall choice with highest score and lowest risk.`}
            </p>

            {/* Key Stats */}
            <div className="flex flex-wrap gap-3">
              <div className="bg-white rounded-xl px-4 py-3 border border-emerald-200 shadow-sm">
                <span className="text-xs text-slate-500 block">RIGHTNAME™ INDEX</span>
                <div className="text-2xl font-black text-emerald-600">{winner.overall_score}/100</div>
              </div>
              <div className="bg-white rounded-xl px-4 py-3 border border-emerald-200 shadow-sm">
                <span className="text-xs text-slate-500 block">Trademark Risk</span>
                <div className="text-2xl font-black text-emerald-600">
                  {winner.trademark_research?.overall_risk_score || 'Low'}/10
                </div>
              </div>
              <div className="bg-white rounded-xl px-4 py-3 border border-emerald-200 shadow-sm">
                <span className="text-xs text-slate-500 block">Success Rate</span>
                <div className="text-2xl font-black text-emerald-600">
                  {winner.trademark_research?.registration_success_probability || 85}%
                </div>
              </div>
              <div className="bg-white rounded-xl px-4 py-3 border border-emerald-200 shadow-sm">
                <span className="text-xs text-slate-500 block">.com Domain</span>
                <div className="text-lg font-bold text-emerald-600">
                  {winner.domain_analysis?.available ? '✅ Available' : '⚠️ Taken'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Backup Recommendation */}
        {backup && (
          <div className="mt-6 pt-4 border-t border-emerald-200">
            <div className="flex items-center gap-3 bg-white/50 rounded-xl p-4">
              <Medal className="w-8 h-8 text-slate-400" />
              <div>
                <span className="text-sm font-medium text-slate-600">🥈 Strong Backup:</span>
                <span className="ml-2 font-bold text-slate-800">&ldquo;{backup.brand_name}&rdquo;</span>
                <span className="ml-2 text-slate-500">({backup.overall_score}/100)</span>
              </div>
            </div>
          </div>
        )}

        {/* Full Rankings Row */}
        {rankings && rankings.length > 1 && (
          <div className="mt-4 pt-4 border-t border-emerald-200">
            <h4 className="text-sm font-bold text-slate-600 mb-3 uppercase tracking-wide">Complete Rankings</h4>
            <div className="flex flex-wrap gap-2">
              {rankings.map((brand, index) => (
                <div
                  key={brand.brand_name}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                    index === 0 
                      ? 'bg-emerald-100 border-2 border-emerald-400 shadow-md' 
                      : brand.rejected 
                        ? 'bg-red-50 border border-red-200'
                        : 'bg-white border border-slate-200'
                  }`}
                >
                  {index === 0 && <Trophy className="w-5 h-5 text-amber-500" />}
                  {index === 1 && !brand.rejected && <Medal className="w-5 h-5 text-slate-400" />}
                  {index === 2 && !brand.rejected && <Award className="w-5 h-5 text-amber-700" />}
                  {brand.rejected && <XCircle className="w-5 h-5 text-red-500" />}
                  <span className={`font-semibold ${
                    brand.rejected ? 'text-red-600 line-through' : 'text-slate-700'
                  }`}>
                    {brand.brand_name}
                  </span>
                  {!brand.rejected && (
                    <Badge variant="outline" className="ml-1">
                      {brand.overall_score}/100
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📊 MASTER COMPARISON TABLE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const MasterComparisonTable = ({ brands }) => {
  const getVerdictStyle = (verdict) => {
    switch(verdict?.toUpperCase()) {
      case 'GO': return 'bg-emerald-100 text-emerald-700 border-emerald-300';
      case 'CONDITIONAL GO': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'CAUTION': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'NO-GO': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'REJECT': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-slate-100 text-slate-700 border-slate-300';
    }
  };

  const getRiskStyle = (risk) => {
    if (risk <= 3) return 'text-emerald-600 font-bold';
    if (risk <= 6) return 'text-amber-600 font-bold';
    return 'text-red-600 font-bold';
  };

  const getDomainStatus = (brand) => {
    if (brand.rejected) return { text: 'N/A', style: 'text-slate-400' };
    const available = brand.domain_analysis?.available || brand.multi_domain_availability?.primary_available;
    if (available) return { text: '✅ Available', style: 'text-emerald-600 font-semibold' };
    return { text: '⚠️ Taken', style: 'text-amber-600' };
  };

  const getSocialCount = (brand) => {
    if (brand.rejected) return 'N/A';
    const social = brand.social_availability || brand.enhanced_social_availability;
    if (!social) return 'N/A';
    const platforms = social.platforms || [];
    const available = platforms.filter(p => p.available).length;
    return `${available}/${platforms.length}`;
  };

  return (
    <Card className="overflow-hidden shadow-lg">
      <CardHeader className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
        <CardTitle className="flex items-center gap-2 text-xl">
          <BarChart3 className="w-6 h-6" />
          Master Comparison Table
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="text-left py-4 px-4 text-sm font-bold text-slate-700 uppercase tracking-wide">Dimension</th>
                {brands.map((brand, idx) => (
                  <th 
                    key={brand.brand_name} 
                    className={`text-center py-4 px-4 text-sm font-bold uppercase tracking-wide ${
                      idx === 0 ? 'bg-emerald-50 text-emerald-800' : 
                      brand.rejected ? 'bg-red-50 text-red-700' : 'text-slate-700'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {idx === 0 && <Trophy className="w-4 h-4 text-amber-500" />}
                      {brand.rejected && <Ban className="w-4 h-4 text-red-500" />}
                      {brand.brand_name}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* RIGHTNAME™ INDEX */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">RIGHTNAME™ INDEX</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    {brand.rejected ? (
                      <span className="text-red-500 font-bold">REJECTED</span>
                    ) : (
                      <span className="text-2xl font-black text-slate-800">{brand.overall_score}<span className="text-sm text-slate-400">/100</span></span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Verdict */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Verdict</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <Badge className={`${getVerdictStyle(brand.verdict)} px-3 py-1 text-sm font-bold`}>
                      {brand.verdict || 'N/A'}
                    </Badge>
                  </td>
                ))}
              </tr>

              {/* Trademark Risk */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Trademark Risk</td>
                {brands.map((brand, idx) => {
                  const risk = brand.rejected ? 10 : (brand.trademark_research?.overall_risk_score || 0);
                  return (
                    <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                      <span className={getRiskStyle(risk)}>{risk}/10</span>
                    </td>
                  );
                })}
              </tr>

              {/* Conflicts Found */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Conflicts Found</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <span className={brand.rejected ? 'text-red-600 font-bold' : 'text-slate-700'}>
                      {brand.rejected ? '50+' : (brand.trademark_research?.total_conflicts_found || 0)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Success Probability */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Success Probability</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <span className={brand.rejected ? 'text-red-500' : 'text-emerald-600 font-semibold'}>
                      {brand.rejected ? '0%' : `${brand.trademark_research?.registration_success_probability || 85}%`}
                    </span>
                  </td>
                ))}
              </tr>

              {/* .com Domain */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">.com Domain</td>
                {brands.map((brand, idx) => {
                  const status = getDomainStatus(brand);
                  return (
                    <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                      <span className={status.style}>{status.text}</span>
                    </td>
                  );
                })}
              </tr>

              {/* Social Handles */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Social Handles</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <span className="text-slate-700">{getSocialCount(brand)}</span>
                  </td>
                ))}
              </tr>

              {/* Name Type */}
              <tr className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">Name Type</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <Badge variant="outline" className="font-medium">
                      {brand.strategic_classification?.split(' ')[0] || brand.brand_classification?.category || 'N/A'}
                    </Badge>
                  </td>
                ))}
              </tr>

              {/* NICE Class */}
              <tr className="hover:bg-slate-50">
                <td className="py-4 px-4 font-semibold text-slate-700">NICE Class</td>
                {brands.map((brand, idx) => (
                  <td key={brand.brand_name} className={`text-center py-4 px-4 ${idx === 0 ? 'bg-emerald-50/50' : brand.rejected ? 'bg-red-50/30' : ''}`}>
                    <span className="text-slate-700">
                      {brand.trademark_research?.nice_classification?.class_number || 'N/A'}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 PER-BRAND MINI-REPORT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const BrandMiniReport = ({ brand, rank, isWinner }) => {
  const [expanded, setExpanded] = useState(isWinner);

  const getRankIcon = () => {
    if (brand.rejected) return <Ban className="w-6 h-6 text-red-500" />;
    if (rank === 1) return <Trophy className="w-6 h-6 text-amber-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-slate-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-700" />;
    return <Star className="w-6 h-6 text-slate-400" />;
  };

  const getHeaderColor = () => {
    if (brand.rejected) return 'from-red-500 to-red-600';
    if (rank === 1) return 'from-emerald-500 to-teal-500';
    if (rank === 2) return 'from-slate-500 to-slate-600';
    return 'from-violet-500 to-purple-500';
  };

  return (
    <Card className={`overflow-hidden shadow-lg transition-all ${brand.rejected ? 'opacity-75' : ''}`}>
      {/* Header */}
      <div 
        className={`bg-gradient-to-r ${getHeaderColor()} p-4 cursor-pointer`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              {getRankIcon()}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-black text-white">{brand.brand_name}</h3>
                {rank === 1 && !brand.rejected && (
                  <Badge className="bg-amber-400 text-amber-900 font-bold">WINNER</Badge>
                )}
                {brand.rejected && (
                  <Badge className="bg-red-200 text-red-800 font-bold">REJECTED</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                {!brand.rejected && (
                  <>
                    <span className="text-white/90 font-bold">{brand.overall_score}/100</span>
                    <Badge className={`${
                      brand.verdict === 'GO' ? 'bg-emerald-400 text-emerald-900' :
                      brand.verdict === 'REJECT' ? 'bg-red-400 text-red-900' :
                      'bg-amber-400 text-amber-900'
                    } font-semibold`}>
                      {brand.verdict}
                    </Badge>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="text-white">
            {expanded ? <ChevronUp className="w-6 h-6" /> : <ChevronDown className="w-6 h-6" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <CardContent className="p-6 space-y-6">
          {/* Summary */}
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-slate-700 leading-relaxed">{brand.summary || 'No summary available.'}</p>
          </div>

          {/* Quick Stats Row */}
          {!brand.rejected && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-emerald-50 rounded-xl p-3 text-center">
                <div className="text-xs text-emerald-600 font-medium">Trademark Risk</div>
                <div className="text-xl font-black text-emerald-700">
                  {brand.trademark_research?.overall_risk_score || 0}/10
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 text-center">
                <div className="text-xs text-blue-600 font-medium">Success Rate</div>
                <div className="text-xl font-black text-blue-700">
                  {brand.trademark_research?.registration_success_probability || 85}%
                </div>
              </div>
              <div className="bg-violet-50 rounded-xl p-3 text-center">
                <div className="text-xs text-violet-600 font-medium">.com Domain</div>
                <div className="text-lg font-bold text-violet-700">
                  {brand.domain_analysis?.available ? '✅ Available' : '⚠️ Taken'}
                </div>
              </div>
              <div className="bg-amber-50 rounded-xl p-3 text-center">
                <div className="text-xs text-amber-600 font-medium">NICE Class</div>
                <div className="text-xl font-black text-amber-700">
                  {brand.trademark_research?.nice_classification?.class_number || 'N/A'}
                </div>
              </div>
            </div>
          )}

          {/* Strengths & Risks */}
          {!brand.rejected && (brand.pros?.length > 0 || brand.cons?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Strengths */}
              {brand.pros?.length > 0 && (
                <div className="bg-emerald-50 rounded-xl p-4">
                  <h4 className="flex items-center gap-2 font-bold text-emerald-700 mb-3">
                    <ThumbsUp className="w-5 h-5" />
                    Key Strengths
                  </h4>
                  <ul className="space-y-2">
                    {brand.pros.slice(0, 4).map((pro, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-emerald-800">
                        <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span>{pro}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Risks */}
              {brand.cons?.length > 0 && (
                <div className="bg-amber-50 rounded-xl p-4">
                  <h4 className="flex items-center gap-2 font-bold text-amber-700 mb-3">
                    <ThumbsDown className="w-5 h-5" />
                    Key Risks
                  </h4>
                  <ul className="space-y-2">
                    {brand.cons.slice(0, 4).map((con, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                        <span>{con}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Dimensions */}
          {!brand.rejected && brand.dimensions?.length > 0 && (
            <div>
              <h4 className="flex items-center gap-2 font-bold text-slate-700 mb-3">
                <BarChart3 className="w-5 h-5 text-violet-600" />
                Dimension Scores
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {brand.dimensions.slice(0, 6).map((dim, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-600 truncate pr-2">
                        {dim.name?.replace(/Brand |& /g, '').substring(0, 20)}
                      </span>
                      <span className="text-sm font-bold text-violet-600">{dim.score}/10</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-violet-500 to-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${(dim.score / 10) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Digital Presence */}
          {!brand.rejected && (
            <div>
              <h4 className="flex items-center gap-2 font-bold text-slate-700 mb-3">
                <Globe className="w-5 h-5 text-cyan-600" />
                Digital Presence
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Domain */}
                <div className="bg-cyan-50 rounded-xl p-4">
                  <h5 className="font-semibold text-cyan-700 mb-2">Domain Availability</h5>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-cyan-800">{brand.brand_name?.toLowerCase()}.com</span>
                      <Badge className={brand.domain_analysis?.available ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                        {brand.domain_analysis?.available ? 'Available' : 'Taken'}
                      </Badge>
                    </div>
                    {brand.multi_domain_availability?.category_domains?.slice(0, 3).map((domain, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-cyan-700">{domain.domain}</span>
                        <Badge variant="outline" className={domain.available ? 'text-emerald-600' : 'text-slate-500'}>
                          {domain.available ? '✓' : '✗'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social */}
                <div className="bg-pink-50 rounded-xl p-4">
                  <h5 className="font-semibold text-pink-700 mb-2">Social Media Handles</h5>
                  <div className="space-y-2">
                    {(brand.social_availability?.platforms || brand.enhanced_social_availability?.platforms || []).slice(0, 4).map((platform, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-pink-800">{platform.platform || platform.name}</span>
                        <Badge variant="outline" className={platform.available ? 'text-emerald-600' : 'text-slate-500'}>
                          {platform.available ? 'Available' : 'Taken'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Alternative Names for Rejected Brands */}
          {(brand.rejected || brand.verdict === 'REJECT' || brand.verdict === 'NO-GO') && brand.alternative_names && (
            <div className="bg-violet-50 rounded-xl p-4">
              <h4 className="flex items-center gap-2 font-bold text-violet-700 mb-3">
                <Lightbulb className="w-5 h-5" />
                Alternative Name Suggestions
              </h4>
              
              {/* Poison Words */}
              {brand.alternative_names.poison_words?.length > 0 && (
                <div className="mb-4">
                  <span className="text-sm font-medium text-red-600">⚠️ Problematic terms: </span>
                  {brand.alternative_names.poison_words.map((word, idx) => (
                    <Badge key={idx} variant="outline" className="mx-1 bg-red-50 text-red-600 border-red-200">
                      {word}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Reasoning */}
              {brand.alternative_names.reasoning && (
                <p className="text-sm text-violet-800 mb-4 bg-white/50 p-3 rounded-lg">
                  {brand.alternative_names.reasoning}
                </p>
              )}

              {/* Suggestions */}
              {brand.alternative_names.suggestions?.length > 0 && (
                <div className="space-y-2">
                  {brand.alternative_names.suggestions.slice(0, 3).map((suggestion, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <span className="font-bold text-violet-800">{suggestion.name}</span>
                        <p className="text-xs text-slate-600 mt-1">{suggestion.rationale}</p>
                      </div>
                      {suggestion.score && (
                        <Badge className="bg-violet-100 text-violet-700">{suggestion.score}/100</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 STRATEGIC PLANNING SECTION - BRAND-NAME INDEPENDENT DATA
// Includes: Market Intelligence, White Space Analysis, NICE Classification,
// Registration Timeline & Costs - These are the same for all brands in the evaluation
// ═══════════════════════════════════════════════════════════════════════════════

const StrategicPlanningSection = ({ brands, category, countries }) => {
  // Get data from the first brand (it's the same for all brands in the evaluation)
  const refBrand = brands.find(b => 
    b.competitor_analysis || 
    b.country_competitor_analysis?.length > 0 || 
    b.nice_classification_strategy ||
    b.realistic_registration_costs
  ) || brands[0];

  if (!refBrand) return null;

  const competitorAnalysis = refBrand.competitor_analysis;
  const countryCompetitorAnalysis = refBrand.country_competitor_analysis || [];
  const niceStrategy = refBrand.nice_classification_strategy;
  const registrationCosts = refBrand.realistic_registration_costs;
  const registrationTimeline = refBrand.registration_timeline;

  // Check if we have any strategic data to show
  const hasWhiteSpace = competitorAnalysis?.white_space_analysis || competitorAnalysis?.strategic_advantage;
  const hasCountryAnalysis = countryCompetitorAnalysis.length > 0;
  const hasNiceStrategy = niceStrategy?.primary_class;
  const hasRegistrationData = registrationCosts || registrationTimeline;

  if (!hasWhiteSpace && !hasCountryAnalysis && !hasNiceStrategy && !hasRegistrationData) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-6 py-3 rounded-full shadow-lg">
          <Map className="w-6 h-6" />
          <h2 className="text-xl font-black">Strategic Planning</h2>
        </div>
        <p className="text-slate-600 mt-2">
          Market intelligence & filing strategy for <span className="font-semibold text-blue-600">{category}</span>
        </p>
      </div>

      {/* 1. WHITE SPACE & STRATEGIC ADVANTAGE */}
      {hasWhiteSpace && (
        <Card className="overflow-hidden border-2 border-emerald-200">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="w-5 h-5" />
              Market Opportunity Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {competitorAnalysis.white_space_analysis && (
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-bold text-emerald-700">White Space Analysis</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {competitorAnalysis.white_space_analysis}
                  </p>
                </div>
              )}
              {competitorAnalysis.strategic_advantage && (
                <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Rocket className="w-5 h-5 text-violet-600" />
                    <h4 className="font-bold text-violet-700">Strategic Advantage</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {competitorAnalysis.strategic_advantage}
                  </p>
                </div>
              )}
            </div>
            {competitorAnalysis.suggested_pricing && (
              <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  <span className="font-bold text-amber-700">Recommended Pricing Strategy</span>
                </div>
                <p className="text-sm text-slate-700">{competitorAnalysis.suggested_pricing}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 2. COUNTRY-SPECIFIC MARKET INTELLIGENCE */}
      {hasCountryAnalysis && (
        <Card className="overflow-hidden border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5" />
              Market Intelligence by Country
              <Badge className="bg-white/20 text-white ml-2">{countryCompetitorAnalysis.length} Markets</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {countryCompetitorAnalysis.map((countryData, idx) => {
                const countryName = typeof countryData.country === 'object' 
                  ? countryData.country?.name 
                  : countryData.country;
                const flag = countryData.country_flag || '🌍';
                
                return (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{flag}</span>
                      <h4 className="font-bold text-slate-800">{countryName}</h4>
                    </div>
                    
                    {/* Competitors in this market */}
                    {countryData.competitors && countryData.competitors.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Top Competitors</p>
                        <div className="flex flex-wrap gap-1">
                          {countryData.competitors.slice(0, 5).map((comp, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-white">
                              {comp.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* White space for this country */}
                    {countryData.white_space_analysis && (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <p className="text-xs font-bold text-emerald-600 mb-1">💡 Opportunity</p>
                        <p className="text-xs text-slate-600">{countryData.white_space_analysis}</p>
                      </div>
                    )}
                    
                    {/* Market entry recommendation */}
                    {countryData.market_entry_recommendation && (
                      <p className="text-xs text-blue-600 mt-2 italic">
                        → {countryData.market_entry_recommendation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. MULTI-CLASS NICE FILING STRATEGY */}
      {hasNiceStrategy && (
        <Card className="overflow-hidden border-2 border-violet-200">
          <CardHeader className="bg-gradient-to-r from-violet-500 to-purple-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5" />
              Multi-Class NICE Filing Strategy
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Primary Class */}
              <div className="p-5 bg-violet-100 rounded-xl border-2 border-violet-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-black text-lg">1</div>
                  <span className="font-black text-violet-800 uppercase tracking-wide">Primary Class</span>
                </div>
                <div className="text-4xl font-black text-violet-700 mb-2">
                  Class {niceStrategy.primary_class?.class_number}
                </div>
                <p className="text-sm text-slate-700">{niceStrategy.primary_class?.description}</p>
                {niceStrategy.primary_class?.term && (
                  <p className="text-xs text-violet-600 mt-2 italic">&ldquo;{niceStrategy.primary_class.term}&rdquo;</p>
                )}
              </div>
              
              {/* Stats */}
              <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-white rounded-lg border border-slate-100">
                    <div className="text-4xl font-black text-violet-600">{niceStrategy.total_classes_recommended}</div>
                    <div className="text-xs text-slate-500 mt-1">Classes Recommended</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg border border-slate-100">
                    <div className="text-4xl font-black text-emerald-600">{niceStrategy.secondary_classes?.length || 0}</div>
                    <div className="text-xs text-slate-500 mt-1">Secondary Classes</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Secondary Classes */}
            {niceStrategy.secondary_classes && niceStrategy.secondary_classes.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Secondary Classes (for comprehensive protection)</p>
                <div className="space-y-2">
                  {niceStrategy.secondary_classes.map((cls, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="w-7 h-7 bg-slate-300 text-slate-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {i + 2}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-800">Class {cls.class_number}</span>
                          {cls.priority && (
                            <Badge variant="outline" className="text-xs">{cls.priority}</Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{cls.description}</p>
                        {cls.rationale && (
                          <p className="text-xs text-blue-600 mt-1">→ {cls.rationale}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Filing Strategy */}
            {niceStrategy.filing_strategy && (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Rocket className="w-4 h-4 text-blue-600" />
                  <span className="font-bold text-blue-700">Recommended Filing Strategy</span>
                </div>
                <p className="text-sm text-slate-700">{niceStrategy.filing_strategy}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 4. REGISTRATION TIMELINE & COSTS */}
      {hasRegistrationData && (
        <Card className="overflow-hidden border-2 border-cyan-200">
          <CardHeader className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5" />
              Registration Timeline & Costs
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Duration */}
            <div className="mb-6">
              <div className="text-3xl font-black text-cyan-700">
                {registrationCosts?.estimated_duration || registrationTimeline?.estimated_duration || '12-18 months'}
              </div>
              <div className="text-sm text-slate-500">Estimated registration duration</div>
            </div>
            
            {/* Stages */}
            {(registrationCosts?.stages || registrationTimeline?.stages)?.length > 0 && (
              <div className="space-y-2 mb-6">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Registration Stages</p>
                {(registrationCosts?.stages || registrationTimeline?.stages).map((stage, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                    <div className="w-8 h-8 bg-cyan-200 rounded-full flex items-center justify-center text-cyan-700 font-bold text-sm">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-800">{stage.stage}</div>
                      <div className="text-xs text-slate-500">{stage.duration}</div>
                    </div>
                    {stage.risk && (
                      <Badge variant="outline" className="text-xs">{stage.risk}</Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {/* Opposition Defense Cost Scenarios */}
            {registrationCosts?.opposition_defense_cost && typeof registrationCosts.opposition_defense_cost === 'object' && (
              <div className="mb-6">
                <p className="text-xs font-bold text-slate-500 uppercase mb-3">Opposition Defense Cost Scenarios</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {Object.entries(registrationCosts.opposition_defense_cost).map(([key, scenario], i) => (
                    <div key={i} className={`p-4 rounded-lg border ${
                      key.includes('appeal') || key.includes('ttab') ? 'bg-red-50 border-red-200' :
                      key.includes('settlement') ? 'bg-amber-50 border-amber-200' :
                      'bg-emerald-50 border-emerald-200'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-slate-700 text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                        <Badge className={`text-xs ${
                          scenario.probability >= 30 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {scenario.probability}% likely
                        </Badge>
                      </div>
                      <div className={`text-xl font-black ${
                        key.includes('appeal') || key.includes('ttab') ? 'text-red-700' :
                        key.includes('settlement') ? 'text-amber-700' :
                        'text-emerald-700'
                      }`}>
                        {scenario.cost}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{scenario.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Cost Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(registrationCosts?.filing_cost_per_class || registrationTimeline?.filing_cost) && (
                <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="text-xs text-slate-500">Filing Cost/Class</div>
                  <div className="font-black text-emerald-700 text-lg">
                    {registrationCosts?.filing_cost_per_class || registrationTimeline?.filing_cost}
                  </div>
                </div>
              )}
              {registrationCosts?.expected_value_cost && (
                <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                  <div className="text-xs text-slate-500">Expected Value Cost</div>
                  <div className="font-black text-violet-700 text-lg">{registrationCosts.expected_value_cost}</div>
                  <div className="text-xs text-slate-400">Probability-weighted</div>
                </div>
              )}
              {registrationCosts?.total_worst_case && (
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="text-xs text-slate-500">Worst Case</div>
                  <div className="font-black text-red-700 text-lg">{registrationCosts.total_worst_case}</div>
                  <div className="text-xs text-slate-400">If appeals required</div>
                </div>
              )}
            </div>
            
            {/* Filing Basis Strategy */}
            {registrationCosts?.filing_basis_strategy && (
              <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="w-4 h-4 text-slate-600" />
                  <span className="font-bold text-slate-700">
                    Filing Basis: {registrationCosts.filing_basis_strategy.recommended_basis}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-3">{registrationCosts.filing_basis_strategy.rationale}</p>
                
                {registrationCosts.filing_basis_strategy.critical_milestones && (
                  <div className="space-y-2">
                    {registrationCosts.filing_basis_strategy.critical_milestones.map((milestone, i) => (
                      <div key={i} className="flex items-center justify-between text-xs p-2 bg-white rounded border border-slate-100">
                        <span className="font-medium text-slate-700">{milestone.milestone}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">{milestone.deadline}</span>
                          {milestone.cost && (
                            <Badge variant="outline" className="text-xs">{milestone.cost}</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 MULTI-CLASS FILING STRATEGY SECTION (Standalone)
// ═══════════════════════════════════════════════════════════════════════════════

const MultiClassFilingSection = ({ brands, category }) => {
  const refBrand = brands.find(b => b.nice_classification_strategy?.primary_class) || brands[0];
  if (!refBrand) return null;
  
  const niceStrategy = refBrand.nice_classification_strategy;
  if (!niceStrategy?.primary_class) return null;

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-6 py-3 rounded-full shadow-lg">
          <FileText className="w-6 h-6" />
          <h2 className="text-xl font-black">Multi-Class NICE Filing Strategy</h2>
        </div>
        <p className="text-slate-600 mt-2">
          Trademark classification for <span className="font-semibold text-violet-600">{category}</span>
        </p>
      </div>
      
      <Card className="overflow-hidden border-2 border-violet-200">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Primary Class */}
            <div className="p-5 bg-violet-100 rounded-xl border-2 border-violet-300">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-10 h-10 bg-violet-600 text-white rounded-full flex items-center justify-center font-black text-lg">1</div>
                <span className="font-black text-violet-800 uppercase tracking-wide">Primary Class</span>
              </div>
              <div className="text-4xl font-black text-violet-700 mb-2">
                Class {niceStrategy.primary_class?.class_number}
              </div>
              <p className="text-sm text-slate-700">{niceStrategy.primary_class?.description}</p>
              {niceStrategy.primary_class?.term && (
                <p className="text-xs text-violet-600 mt-2 italic">&ldquo;{niceStrategy.primary_class.term}&rdquo;</p>
              )}
            </div>
            
            {/* Stats */}
            <div className="p-5 bg-slate-50 rounded-xl border border-slate-200">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-white rounded-lg border border-slate-100">
                  <div className="text-4xl font-black text-violet-600">{niceStrategy.total_classes_recommended}</div>
                  <div className="text-xs text-slate-500 mt-1">Classes Recommended</div>
                </div>
                <div className="text-center p-4 bg-white rounded-lg border border-slate-100">
                  <div className="text-4xl font-black text-emerald-600">{niceStrategy.secondary_classes?.length || 0}</div>
                  <div className="text-xs text-slate-500 mt-1">Secondary Classes</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Secondary Classes */}
          {niceStrategy.secondary_classes && niceStrategy.secondary_classes.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-bold text-slate-500 uppercase mb-3">Secondary Classes (for comprehensive protection)</p>
              <div className="space-y-2">
                {niceStrategy.secondary_classes.map((cls, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="w-7 h-7 bg-slate-300 text-slate-700 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {i + 2}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-slate-800">Class {cls.class_number}</span>
                        {cls.priority && (
                          <Badge variant="outline" className="text-xs">{cls.priority}</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{cls.description}</p>
                      {cls.rationale && (
                        <p className="text-xs text-blue-600 mt-1">→ {cls.rationale}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Filing Strategy */}
          {niceStrategy.filing_strategy && (
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Rocket className="w-4 h-4 text-blue-600" />
                <span className="font-bold text-blue-700">Recommended Filing Strategy</span>
              </div>
              <p className="text-sm text-slate-700">{niceStrategy.filing_strategy}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🌍 MARKET INTELLIGENCE SECTION (Standalone)
// ═══════════════════════════════════════════════════════════════════════════════

const MarketIntelligenceSection = ({ brands, category }) => {
  const refBrand = brands.find(b => b.country_competitor_analysis?.length > 0) || brands[0];
  if (!refBrand) return null;
  
  const countryCompetitorAnalysis = refBrand.country_competitor_analysis || [];
  const competitorAnalysis = refBrand.competitor_analysis;
  
  const hasCountryAnalysis = countryCompetitorAnalysis.length > 0;
  const hasWhiteSpace = competitorAnalysis?.white_space_analysis || competitorAnalysis?.strategic_advantage;
  
  if (!hasCountryAnalysis && !hasWhiteSpace) return null;

  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-full shadow-lg">
          <Globe className="w-6 h-6" />
          <h2 className="text-xl font-black">Market Intelligence</h2>
        </div>
        <p className="text-slate-600 mt-2">
          Market opportunities for <span className="font-semibold text-blue-600">{category}</span>
        </p>
      </div>
      
      {/* WHITE SPACE & STRATEGIC ADVANTAGE */}
      {hasWhiteSpace && (
        <Card className="overflow-hidden border-2 border-emerald-200">
          <CardHeader className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="w-5 h-5" />
              Market Opportunity Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {competitorAnalysis.white_space_analysis && (
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="w-5 h-5 text-emerald-600" />
                    <h4 className="font-bold text-emerald-700">White Space Analysis</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {competitorAnalysis.white_space_analysis}
                  </p>
                </div>
              )}
              {competitorAnalysis.strategic_advantage && (
                <div className="bg-violet-50 rounded-xl p-5 border border-violet-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Rocket className="w-5 h-5 text-violet-600" />
                    <h4 className="font-bold text-violet-700">Strategic Advantage</h4>
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {competitorAnalysis.strategic_advantage}
                  </p>
                </div>
              )}
            </div>
            {competitorAnalysis.suggested_pricing && (
              <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4 text-amber-600" />
                  <span className="font-bold text-amber-700">Recommended Pricing Strategy</span>
                </div>
                <p className="text-sm text-slate-700">{competitorAnalysis.suggested_pricing}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* COUNTRY-SPECIFIC MARKET INTELLIGENCE */}
      {hasCountryAnalysis && (
        <Card className="overflow-hidden border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white py-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="w-5 h-5" />
              Market Intelligence by Country
              <Badge className="bg-white/20 text-white ml-2">{countryCompetitorAnalysis.length} Markets</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {countryCompetitorAnalysis.map((countryData, idx) => {
                const countryName = typeof countryData.country === 'object' 
                  ? countryData.country?.name 
                  : countryData.country;
                const flag = countryData.country_flag || '🌍';
                
                return (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{flag}</span>
                      <h4 className="font-bold text-slate-800">{countryName}</h4>
                    </div>
                    
                    {/* Competitors in this market */}
                    {countryData.competitors && countryData.competitors.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-2">Top Competitors</p>
                        <div className="flex flex-wrap gap-1">
                          {countryData.competitors.slice(0, 5).map((comp, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-white">
                              {comp.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* White space for this country */}
                    {countryData.white_space_analysis && (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <p className="text-xs font-bold text-emerald-600 mb-1">💡 Opportunity</p>
                        <p className="text-xs text-slate-600">{countryData.white_space_analysis}</p>
                      </div>
                    )}
                    
                    {/* Market entry recommendation */}
                    {countryData.market_entry_recommendation && (
                      <p className="text-xs text-blue-600 mt-2 italic">
                        → {countryData.market_entry_recommendation}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 📋 REGISTRATION TIMELINE SECTION (Standalone - kept in Strategic Planning area)
// ═══════════════════════════════════════════════════════════════════════════════

const RegistrationTimelineSection = ({ brands }) => {
  const refBrand = brands.find(b => b.realistic_registration_costs || b.registration_timeline) || brands[0];
  if (!refBrand) return null;
  
  const registrationCosts = refBrand.realistic_registration_costs;
  const registrationTimeline = refBrand.registration_timeline;
  
  if (!registrationCosts && !registrationTimeline) return null;

  return (
    <Card className="overflow-hidden border-2 border-cyan-200">
      <CardHeader className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="w-5 h-5" />
          Registration Timeline & Costs
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        {/* Duration */}
        <div className="mb-6">
          <div className="text-3xl font-black text-cyan-700">
            {registrationCosts?.estimated_duration || registrationTimeline?.estimated_duration || '12-18 months'}
          </div>
          <div className="text-sm text-slate-500">Estimated registration duration</div>
        </div>
        
        {/* Stages */}
        {(registrationCosts?.stages || registrationTimeline?.stages)?.length > 0 && (
          <div className="space-y-2 mb-6">
            <p className="text-xs font-bold text-slate-500 uppercase mb-3">Registration Stages</p>
            {(registrationCosts?.stages || registrationTimeline?.stages).map((stage, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-cyan-50 rounded-lg border border-cyan-100">
                <div className="w-8 h-8 bg-cyan-200 rounded-full flex items-center justify-center text-cyan-700 font-bold text-sm">
                  {i + 1}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-slate-800">{stage.stage}</div>
                  <div className="text-xs text-slate-500">{stage.duration}</div>
                </div>
                {stage.risk && (
                  <Badge variant="outline" className="text-xs">{stage.risk}</Badge>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Cost Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(registrationCosts?.filing_cost_per_class || registrationTimeline?.filing_cost) && (
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="text-xs text-slate-500">Filing Cost/Class</div>
              <div className="font-black text-emerald-700 text-lg">
                {registrationCosts?.filing_cost_per_class || registrationTimeline?.filing_cost}
              </div>
            </div>
          )}
          {registrationCosts?.expected_value_cost && (
            <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
              <div className="text-xs text-slate-500">Expected Value Cost</div>
              <div className="font-black text-violet-700 text-lg">{registrationCosts.expected_value_cost}</div>
              <div className="text-xs text-slate-400">Probability-weighted</div>
            </div>
          )}
          {registrationCosts?.total_worst_case && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="text-xs text-slate-500">Worst Case</div>
              <div className="font-black text-red-700 text-lg">{registrationCosts.total_worst_case}</div>
              <div className="text-xs text-slate-400">If appeals required</div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎯 DECISION MATRIX COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const DecisionMatrix = ({ brands }) => {
  const validBrands = brands.filter(b => !b.rejected);
  if (validBrands.length < 2) return null;

  // Sort by different criteria
  const byScore = [...validBrands].sort((a, b) => (b.overall_score || 0) - (a.overall_score || 0))[0];
  const byRisk = [...validBrands].sort((a, b) => 
    (a.trademark_research?.overall_risk_score || 10) - (b.trademark_research?.overall_risk_score || 10)
  )[0];
  const byDomain = validBrands.find(b => b.domain_analysis?.available) || validBrands[0];

  const decisions = [
    { priority: 'Lowest legal risk', choose: byRisk.brand_name, reason: `Only ${byRisk.trademark_research?.overall_risk_score || 0}/10 risk score` },
    { priority: 'Highest RIGHTNAME™ INDEX', choose: byScore.brand_name, reason: `Top index of ${byScore.overall_score}/100` },
    { priority: 'Best domain availability', choose: byDomain.brand_name, reason: byDomain.domain_analysis?.available ? '.com available' : 'Best alternatives' },
  ];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5" />
          Decision Matrix
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full">
          <thead className="bg-amber-50">
            <tr>
              <th className="text-left py-3 px-4 text-sm font-bold text-amber-800">If your priority is...</th>
              <th className="text-left py-3 px-4 text-sm font-bold text-amber-800">Choose</th>
              <th className="text-left py-3 px-4 text-sm font-bold text-amber-800">Because</th>
            </tr>
          </thead>
          <tbody>
            {decisions.map((decision, idx) => (
              <tr key={idx} className={`border-b border-amber-100 ${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}`}>
                <td className="py-3 px-4 text-sm text-slate-700 font-medium">{decision.priority}</td>
                <td className="py-3 px-4">
                  <Badge className="bg-amber-100 text-amber-800 font-bold">{decision.choose}</Badge>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600">{decision.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎯 MAIN COMPARISON DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const ComparisonDashboard = ({ data }) => {
  if (!data || !data.brand_scores || data.brand_scores.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">No comparison data available</p>
      </div>
    );
  }

  // Normalize brand_scores
  const brandScores = data.brand_scores.map(b => ({
    ...b,
    overall_score: b.overall_score || b.namescore || 0,
    rejected: b.rejected || b.verdict === 'REJECT' || b.verdict === 'NO-GO'
  }));
  
  // Sort by score (non-rejected first, then by score)
  const sortedBrands = [...brandScores].sort((a, b) => {
    if (a.rejected && !b.rejected) return 1;
    if (!a.rejected && b.rejected) return -1;
    return (b.overall_score || 0) - (a.overall_score || 0);
  });

  const winner = sortedBrands.find(b => !b.rejected);
  const backup = sortedBrands.filter(b => !b.rejected)[1] || null;
  const hasRejected = sortedBrands.some(b => b.rejected);

  return (
    <div className="space-y-8 p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-black text-slate-800 mb-2">
          🔍 Brand Comparison Report
        </h1>
        <p className="text-slate-600 text-lg">
          Analyzing {brandScores.length} brand names for <span className="font-semibold text-violet-600">{data.category || 'your business'}</span>
        </p>
      </div>

      {/* Winner Card */}
      <WinnerCard winner={winner} rankings={sortedBrands} backup={backup} />

      {/* Rejected Warning */}
      {hasRejected && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h4 className="font-bold text-red-800 text-lg">⚠️ Some names were rejected</h4>
            <p className="text-red-600 mt-1">
              {sortedBrands.filter(b => b.rejected).map(b => `"${b.brand_name}"`).join(', ')} 
              {' '}matched existing brands or have critical conflicts. See alternative suggestions below.
            </p>
          </div>
        </div>
      )}

      {/* Master Comparison Table */}
      <MasterComparisonTable brands={sortedBrands} />

      {/* Decision Matrix */}
      <DecisionMatrix brands={sortedBrands} />

      {/* ════════════════════ PAGE 4: PER-BRAND MINI REPORTS ════════════════════ */}
      <div>
        <h2 className="text-2xl font-black text-slate-800 mb-4 flex items-center gap-2">
          <FileText className="w-6 h-6 text-violet-600" />
          Detailed Analysis Per Brand
        </h2>
        <div className="space-y-4">
          {sortedBrands.map((brand, idx) => (
            <BrandMiniReport 
              key={brand.brand_name} 
              brand={brand} 
              rank={idx + 1}
              isWinner={idx === 0 && !brand.rejected}
            />
          ))}
        </div>
      </div>

      {/* ════════════════════ PAGE 5: UNIVERSAL LINGUISTIC ANALYSIS ════════════════════ */}
      <LinguisticComparisonSection brands={sortedBrands} />

      {/* ════════════════════ PAGE 6: MULTI-CLASS FILING STRATEGY ════════════════════ */}
      <MultiClassFilingSection brands={sortedBrands} category={data.category} />

      {/* ════════════════════ PAGE 7: COMPETITIVE LANDSCAPE ════════════════════ */}
      <CompetitiveLandscapeSection brands={sortedBrands} category={data.category} />

      {/* ════════════════════ PAGE 8: MARKET INTELLIGENCE ════════════════════ */}
      <MarketIntelligenceSection brands={sortedBrands} category={data.category} />

      {/* Final Recommendation */}
      <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200 shadow-xl">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shadow-lg">
              <Scale className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-black text-slate-800 mb-2">📋 Final Recommendation</h3>
              {winner ? (
                <div className="space-y-3">
                  <p className="text-slate-700 leading-relaxed">
                    Based on our comprehensive analysis across trademark risk, digital availability, 
                    linguistic quality, and cultural fit, we recommend <strong className="text-violet-700">&ldquo;{winner.brand_name}&rdquo;</strong> as 
                    your brand name.
                  </p>
                  <div className="bg-white/60 rounded-lg p-4">
                    <h4 className="font-bold text-slate-700 mb-2">Why {winner.brand_name}?</h4>
                    <ul className="space-y-1 text-sm text-slate-600">
                      <li>✅ Highest RIGHTNAME™ INDEX: {winner.overall_score}/100</li>
                      <li>✅ Trademark risk: {winner.trademark_research?.overall_risk_score || 'Low'}/10</li>
                      <li>✅ Registration success: {winner.trademark_research?.registration_success_probability || 85}%</li>
                      <li>✅ .com domain: {winner.domain_analysis?.available ? 'Available' : 'Alternatives available'}</li>
                    </ul>
                  </div>
                  {backup && (
                    <p className="text-sm text-slate-600 bg-white/40 rounded-lg p-3">
                      💡 <strong>Backup option:</strong> If &ldquo;{winner.brand_name}&rdquo; doesn&apos;t resonate, consider &ldquo;{backup.brand_name}&rdquo; 
                      (RIGHTNAME™ INDEX: {backup.overall_score}/100) as your second choice.
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-red-50 rounded-lg p-4">
                  <p className="text-red-700">
                    Unfortunately, all submitted brand names were rejected due to existing trademark conflicts. 
                    Please review the alternative suggestions provided for each rejected name, or try completely different name variations.
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ComparisonDashboard;
